import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaCircleCheck, FaClock, FaCircleXmark, FaCalendarCheck, FaHand } from "react-icons/fa6";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { markOnboardStepInProgress } from "../api/settings.js";
import { fetchOnboardSteps } from "../store/app/action.js";
import { onboardChecklistItems } from "@/utils/Utils.js";
import { manageAPI } from "../api/index.js";
import { publishStore } from "../api/admin.js";
import { addToast, getMerchants } from "../store/app/action.js";
const checklistItems = onboardChecklistItems;

const OnboardChecklist = ({ onSubmit, loading = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { onboard_steps, selectedLocation } = useSelector((state) => state.app);

  // Fetch onboard steps whenever the component loads or selectedLocation changes
  useEffect(() => {
    dispatch(fetchOnboardSteps());
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [incompleteStepsModalOpen, setIncompleteStepsModalOpen] = useState(false);
  const [publishOption, setPublishOption] = useState("immediate"); // "immediate", "schedule", "manual"
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [completingStep, setCompletingStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Get step statuses from Redux state
  // Values can be: 'in_progress', 'completed', or undefined (not started)
  const stepStatuses = useMemo(() => {
    if (!onboard_steps) return {};
    if (Array.isArray(onboard_steps)) {
      const steps = {};
      onboard_steps.forEach((step) => {
        if (step.step) {
          // Preserve the status value if it exists, otherwise default to 'completed' for backward compatibility
          steps[step.step] = step.status || 'completed';
        }
      });
      return steps;
    }
    // If it's an object, it might have a completed_steps array or similar
    if (onboard_steps.completed_steps) {
      const steps = {};
      onboard_steps.completed_steps.forEach((step) => {
        if (typeof step === 'string') {
          steps[step] = 'completed'; // Default to completed for backward compatibility
        } else if (step.step) {
          steps[step.step] = step.status || 'completed';
        }
      });
      return steps;
    }
    // If it's already a flat object with step keys and status values
    // Check if it has a 'steps' property (nested structure)
    if (onboard_steps.steps) {
      return onboard_steps.steps;
    }
    // If it's already a flat object with step keys
    return onboard_steps;
  }, [onboard_steps]);

  // Initialize checkedItems from Redux state
  const checkedItems = useMemo(() => {
    const items = {};
    checklistItems.forEach((item) => {
      items[item.key] = stepStatuses[item.key] || undefined;
    });
    return items;
  }, [stepStatuses]);

  const handleItemClick = async (item) => {
    // Check if prerequisite is met
    if (item.prerequisite) {
      const prerequisiteStatus = stepStatuses[item.prerequisite];
      if (prerequisiteStatus !== 'completed') {
        // Prerequisite not met, don't allow click
        return;
      }
    }
    
    // If step is not completed, mark it as in progress
    const currentStatus = stepStatuses[item.key];
    if (currentStatus !== 'completed' && !item.skipInProgress) {
      try {
        setCompletingStep(item.key);
        await markOnboardStepInProgress(item.key, item.markComplete);
        
        // Refresh onboard steps from API
        dispatch(fetchOnboardSteps());
      } catch (error) {

      } finally {
        setCompletingStep(null);
      }
    }
    
    // Navigate to the link if provided
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleSubmit = () => {
    if (!allRequiredStepsCompleted) {
      setIncompleteStepsModalOpen(true);
      return;
    }
    setModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedLocation?.id) {
      dispatch(addToast({
        type: "error",
        message: "Store information not available. Please refresh the page.",
        timer: 5
      }));
      return;
    }

    // Validate schedule fields if schedule option is selected
    if (publishOption === "schedule" && (!scheduledDate || !scheduledTime)) {
      dispatch(addToast({
        type: "error",
        message: "Please select both date and time for scheduled publishing",
        timer: 5
      }));
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        publish_option: publishOption,
        ...(publishOption === "schedule" && {
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime,
        }),
      };

      await manageAPI.post(`merchant/${selectedLocation.id}/submit-for-review`, payload);
      
      // Refresh onboard steps to get updated status
      dispatch(fetchOnboardSteps());
      
      // Close the submit modal and show success modal
      setModalOpen(false);
      setSuccessModalOpen(true);
      
      // Reset form
      setPublishOption("immediate");
      setScheduledDate("");
      setScheduledTime("");
    } catch (error) {
      console.error('Error submitting for review:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to submit for review. Please try again.";
      dispatch(addToast({
        type: "error",
        message: errorMessage,
        timer: 5
      }));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Check if steps should be disabled (if status is set and not rejected)
  const stepsDisabled = onboard_steps?.status && onboard_steps?.status !== 'rejected';
  
  // Check if all required steps are completed
  const allRequiredStepsCompleted = useMemo(() => {
    const requiredItems = checklistItems.filter(item => item.required === true);
    return requiredItems.every(item => {
      const stepStatus = stepStatuses[item.key];
      return stepStatus === 'completed';
    });
  }, [stepStatuses]);

  // Get list of incomplete required steps
  const incompleteRequiredSteps = useMemo(() => {
    return checklistItems
      .filter(item => item.required === true)
      .filter(item => {
        const stepStatus = stepStatuses[item.key];
        return stepStatus !== 'completed';
      })
      .map(item => item.label);
  }, [stepStatuses]);
  
  const handlePublishNow = async () => {
    if (!selectedLocation?.id) {
      dispatch(addToast({
        type: "error",
        message: "Store information not available. Please refresh the page.",
        timer: 5
      }));
      return;
    }

    setPublishing(true);
    try {
      await publishStore();
      
      // Refresh onboard steps to get updated status
      dispatch(fetchOnboardSteps());
      dispatch(getMerchants());
      navigate("/");
      
      dispatch(addToast({
        type: "success",
        message: "Your store has been published successfully!",
        timer: 5
      }));
    } catch (error) {
      console.error('Error publishing store:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to publish store. Please try again.";
      dispatch(addToast({
        type: "error",
        message: errorMessage,
        timer: 5
      }));
    } finally {
      setPublishing(false);
    }
  };
  
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Onboarding Checklist
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Complete these tasks to finish setting up your account
          </p>
        </div>
      </div>

      {/* Status Box */}
      {onboard_steps?.status && (
        <div className={`shadow-lg rounded-sm border p-6 mb-6 ${
          onboard_steps.status === 'rejected' 
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : onboard_steps.status === 'scheduled_publish' || onboard_steps.status === 'awaiting_manual_publish'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-white dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700'
        }`}>
          {onboard_steps.status === 'submitted' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <FaClock className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                  We are reviewing your submission
                </h2>
              {onboard_steps.submitted_at && (
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Submitted on {new Date(onboard_steps.submitted_at).toLocaleString()}
                </p>
              )}
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400">
                Approval time may vary based on the complexity of the submission. You can check back on this page for updates. We will also send you an email when the status changes.
                {onboard_steps.publish_option && (
                  <>
                    {' '}
                    {onboard_steps.publish_option === 'immediate' && 'Once approved, your store will be published immediately.'}
                    {onboard_steps.publish_option === 'schedule' && (
                      <>
                        Once approved, your store will be published{' '}
                        {onboard_steps.scheduled_date && onboard_steps.scheduled_time ? (
                          <>on {new Date(`${onboard_steps.scheduled_date}T${onboard_steps.scheduled_time}`).toLocaleString()}.</>
                        ) : (
                          'at the scheduled time.'
                        )}
                      </>
                    )}
                    {onboard_steps.publish_option === 'manual' && 'Once approved, you can publish your store manually when you\'re ready.'}
                  </>
                )}
              </p>
            </div>
          )}

          {onboard_steps.status === 'rejected' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                <FaCircleXmark className="w-5 h-5 text-red-600 dark:text-red-400" />
                Submission Rejected
              </h2>
              {onboard_steps.reject_reason && (
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Rejection Reason:</p>
                  <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
                    {onboard_steps.reject_reason}
                  </p>
                </div>
              )}
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Please correct the problems before resubmitting. If you require additional clarification, message our team at{' '}
                <a href="mailto:merchant-support@sauceydirect.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                  merchant-support@sauceydirect.com
                </a>
              </p>
            </div>
          )}

          {onboard_steps.status === 'scheduled_publish' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
                <FaCalendarCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                Submission Approved
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                We've approved your submission. Your store will be published at{' '}
                {onboard_steps.scheduled_date && onboard_steps.scheduled_time ? (
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    {new Date(`${onboard_steps.scheduled_date}T${onboard_steps.scheduled_time}`).toLocaleString()}
                  </span>
                ) : (
                  <span className="font-medium text-slate-800 dark:text-slate-100">the scheduled time</span>
                )}
                .
              </p>
              <div>
                <PrimaryButton
                  onClick={handlePublishNow}
                  loading={publishing}
                  disabled={publishing}
                >
                  Publish Now
                </PrimaryButton>
              </div>
            </div>
          )}

          {onboard_steps.status === 'awaiting_manual_publish' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
                <FaHand className="w-5 h-5 text-green-600 dark:text-green-400" />
                Submission Approved
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                We've approved your submission. You can now publish your store manually when you're ready.
              </p>
              <div>
                <PrimaryButton
                  onClick={handlePublishNow}
                  loading={publishing}
                  disabled={publishing}
                >
                  Publish Now
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="space-y-4">
          {checklistItems.map((item) => {
            const stepStatus = stepStatuses[item.key];
            const isCompleted = stepStatus === 'completed';
            const isInProgress = stepStatus === 'in_progress';
            const isCompleting = completingStep === item.key;
            
            // Check if prerequisite is met
            const hasPrerequisite = !!item.prerequisite;
            const prerequisiteStatus = hasPrerequisite ? stepStatuses[item.prerequisite] : null;
            const prerequisiteMet = !hasPrerequisite || prerequisiteStatus === 'completed';
            const prerequisiteItem = hasPrerequisite ? checklistItems.find(i => i.key === item.prerequisite) : null;
            
            const isDisabled = stepsDisabled || isCompleting || !prerequisiteMet;
            
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleItemClick(item)}
                disabled={isDisabled}
                className={`w-full flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg transition-colors text-left ${
                  stepsDisabled 
                    ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800/30' 
                    : isDisabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {isCompleting ? (
                    <svg className="animate-spin w-5 h-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <FaCircleCheck
                      className={`w-5 h-5 ${
                        isCompleted
                          ? "text-green-600 dark:text-green-400"
                          : isInProgress
                          ? "text-yellow-500 dark:text-yellow-400"
                          : "text-slate-300 dark:text-slate-600"
                      }`}
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {item.label}
                    {item.required && (
                      <span className="text-red-500 dark:text-red-400 ml-1">*</span>
                    )}
                    {isInProgress && (
                      <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400 font-normal">
                        (In Progress)
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {item.description}
                    </div>
                  )}
                  {!prerequisiteMet && prerequisiteItem && (
                    <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                      Complete "{prerequisiteItem.label}" first
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-700">
          <PrimaryButton
            onClick={handleSubmit}
            loading={loading}
            disabled={onboard_steps?.status && onboard_steps?.status !== 'rejected'}
            width="full"
          >
            Submit for review
          </PrimaryButton>
        </div>
      </div>

      {/* Submit for Review Modal */}
      <ModalBasic
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        width="md"
        title="Submit for Review"
      >
        <div className="space-y-6">
          <p className="text-slate-600 dark:text-slate-400">
            When your store is approved by Saucey, how would you like to publish it?
          </p>

          <div className="space-y-3">
            {/* Publish Immediately */}
            <label className="flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <input
                type="radio"
                name="publishOption"
                value="immediate"
                checked={publishOption === "immediate"}
                onChange={(e) => setPublishOption(e.target.value)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  Publish Store Immediately
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  Your store will go live automatically once approved
                </div>
              </div>
            </label>

            {/* Schedule Publish Time */}
            {/* <label className="flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <input
                type="radio"
                name="publishOption"
                value="schedule"
                checked={publishOption === "schedule"}
                onChange={(e) => setPublishOption(e.target.value)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  Publish automatically after
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  We will publish your store no earlier than the selected date and time after approval.  If we approve your store after the selected date and time, it will be published immediately.
                </div>
                {publishOption === "schedule" && (
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-slate-600 dark:text-slate-400 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        className="form-input w-full text-sm"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-slate-600 dark:text-slate-400 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        className="form-input w-full text-sm"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
            </label> */}

            {/* Manually Publish */}
            <label className="flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <input
                type="radio"
                name="publishOption"
                value="manual"
                checked={publishOption === "manual"}
                onChange={(e) => setPublishOption(e.target.value)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  Manually Publish
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  You'll publish your store manually after approval
                </div>
              </div>
            </label>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton 
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleConfirmSubmit}
              disabled={submitting || (publishOption === "schedule" && (!scheduledDate || !scheduledTime))}
              loading={submitting}
            >
              Submit for Review
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* Incomplete Required Steps Modal */}
      <ModalBasic
        modalOpen={incompleteStepsModalOpen}
        setModalOpen={setIncompleteStepsModalOpen}
        width="md"
        title="Complete Required Steps"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Please complete all required steps
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              The following required steps must be completed before you can submit for review:
            </p>
            <ul className="text-left list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-2 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
              {incompleteRequiredSteps.map((stepLabel, index) => (
                <li key={index}>{stepLabel}</li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <PrimaryButton onClick={() => setIncompleteStepsModalOpen(false)}>
              Close
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>

      {/* Success Modal */}
      <ModalBasic
        modalOpen={successModalOpen}
        setModalOpen={setSuccessModalOpen}
        width="md"
        title="Submitted Successfully"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Your store has been submitted for review!
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Our team will review your submission and get back to you soon. You'll receive an email notification once your store has been reviewed.
            </p>
            {publishOption === "immediate" && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                Once approved, your store will be published immediately.
              </p>
            )}
            {publishOption === "schedule" && scheduledDate && scheduledTime && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                Once approved, your store will be published automatically on {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}.
              </p>
            )}
            {publishOption === "manual" && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                Once approved, you'll be able to publish your store manually when you're ready.
              </p>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
            <PrimaryButton onClick={() => setSuccessModalOpen(false)}>
              Close
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default OnboardChecklist;


